/**
 * Book of Lessons — alpha catalog.
 *
 * 30 lessons covering the first 25–35 minutes of play, plus a handful of
 * deeper unlocks reachable in 1–2 sessions. Source: `docs/09` §VII.3.
 *
 * `bonus` is the additive component fed into the global multiplier.
 * `0.01` = +1 %. Lesson bonuses stack (sum, then participate in the
 * multiplicative composition in `game-core/multipliers`).
 */
import type { LessonDefinition } from '@sai-clicker/game-core';

export const LESSONS_ALPHA: readonly LessonDefinition[] = [
  {
    id: 'L01',
    title: 'Первая искра',
    epoch: 1,
    condition: { kind: 'firstTap' },
    bonus: 0.01,
  },
  {
    id: 'L02',
    title: 'Сто',
    epoch: 1,
    condition: { kind: 'awarenessesAtLeast', value: 100 },
    bonus: 0.01,
  },
  {
    id: 'L03',
    title: 'Тысяча',
    epoch: 1,
    condition: { kind: 'awarenessesAtLeast', value: 1_000 },
    bonus: 0.02,
  },
  {
    id: 'L04',
    title: 'Кристалл',
    epoch: 1,
    condition: { kind: 'crystallizationsAtLeast', value: 1 },
    bonus: 0.02,
  },
  {
    id: 'L05',
    title: 'Серия 10',
    epoch: 1,
    condition: { kind: 'tapsInRhythm', value: 10 },
    bonus: 0.01,
  },
  {
    id: 'L06',
    title: 'Серия 50',
    epoch: 1,
    condition: { kind: 'tapsInRhythm', value: 50 },
    bonus: 0.02,
  },
  {
    id: 'L07',
    title: 'Утренний свет',
    epoch: 1,
    condition: { kind: 'tapAtHour', fromHour: 6, toHour: 9 },
    bonus: 0.01,
  },
  {
    id: 'L08',
    title: 'Тёплая ночь',
    epoch: 1,
    condition: { kind: 'tapAtHour', fromHour: 22, toHour: 25 },
    bonus: 0.01,
  },
  {
    id: 'L09',
    title: 'Возвращение',
    epoch: 1,
    condition: { kind: 'returnAfterHours', value: 12 },
    bonus: 0.03,
  },
  {
    id: 'L10',
    title: 'Семь дней',
    epoch: 1,
    condition: { kind: 'daysStreak', value: 7 },
    bonus: 0.05,
  },
  {
    id: 'L11',
    title: 'Шёпот',
    epoch: 1,
    condition: { kind: 'collectPhrasesInEpoch', count: 10, epoch: 1 },
    bonus: 0.02,
  },
  {
    id: 'L12',
    title: 'Голос земли',
    epoch: 1,
    condition: { kind: 'collectAllPhrasesInLayer', layer: 'elemental' },
    bonus: 0.03,
  },
  {
    id: 'L13',
    title: 'Тело-дом',
    epoch: 1,
    condition: { kind: 'collectAllPhrasesInLayer', layer: 'body' },
    bonus: 0.03,
  },
  {
    id: 'L14',
    title: 'Глаза-уши',
    epoch: 1,
    condition: { kind: 'collectAllPhrasesInLayer', layer: 'sensory' },
    bonus: 0.03,
  },
  {
    id: 'L15',
    title: 'Имя',
    epoch: 1,
    condition: { kind: 'profileNameSet' },
    bonus: 0.02,
  },
  {
    id: 'L16',
    title: 'Искра в небе',
    epoch: 1,
    condition: { kind: 'goldenSparkCaught', count: 1 },
    bonus: 0.02,
  },
  {
    id: 'L17',
    title: 'Три Золотых',
    epoch: 1,
    condition: { kind: 'goldenSparkCaught', count: 3 },
    bonus: 0.03,
  },
  {
    id: 'L18',
    title: 'Эпоха II',
    epoch: 1,
    condition: { kind: 'reachEpoch', epoch: 2 },
    bonus: 0.05,
  },
  {
    id: 'L19',
    title: 'Дыхание',
    epoch: 2,
    condition: { kind: 'awarenessesAtLeast', value: 5_000 },
    bonus: 0.02,
  },
  {
    id: 'L20',
    title: 'Серия 100',
    epoch: 2,
    condition: { kind: 'tapsInRhythm', value: 100 },
    bonus: 0.03,
  },
  {
    id: 'L21',
    title: 'Глубина 3',
    epoch: 2,
    condition: { kind: 'awarenessesAtLeast', value: 25_000 },
    bonus: 0.03,
  },
  {
    id: 'L22',
    title: 'Эпоха III',
    epoch: 2,
    condition: { kind: 'reachEpoch', epoch: 3 },
    bonus: 0.05,
  },
  {
    id: 'L23',
    title: 'ФРАКТАЛiТи',
    epoch: 3,
    condition: { kind: 'crystallizationsAtLeast', value: 3 },
    bonus: 0.03,
  },
  {
    id: 'L24',
    title: 'Ветвь до конца',
    epoch: 3,
    condition: { kind: 'spiritBallsAtLeast', value: 60 },
    bonus: 0.03,
  },
  {
    id: 'L25',
    title: 'Возврат к запросу',
    epoch: 3,
    condition: { kind: 'returnAfterHours', value: 168 }, // 7 days
    bonus: 0.05,
  },
  {
    id: 'L26',
    title: 'Полная эпоха I',
    epoch: 1,
    condition: { kind: 'collectAllPhrasesInEpoch', epoch: 1 },
    bonus: 0.1,
  },
  {
    id: 'L27',
    title: 'Полная эпоха II',
    epoch: 2,
    condition: { kind: 'collectAllPhrasesInEpoch', epoch: 2 },
    bonus: 0.1,
  },
  {
    id: 'L28',
    title: 'Полная эпоха III',
    epoch: 3,
    condition: { kind: 'collectAllPhrasesInEpoch', epoch: 3 },
    bonus: 0.1,
  },
  {
    id: 'L29',
    title: 'Первый Проводник',
    epoch: 2,
    condition: { kind: 'spiritBallsAtLeast', value: 20 },
    bonus: 0.05,
  },
  {
    id: 'L30',
    title: 'Тишина',
    epoch: 1,
    condition: { kind: 'silenceSecondsAtLeast', value: 300 },
    bonus: 0.03,
    hidden: true,
  },

  // ===== Эпоха 3 — ФРАКТАЛiТи =====
  // JB doc 26 audit §1: эпоха 3 контент пуст. 5 уроков baseline.
  {
    id: 'L31',
    title: 'Десять тысяч касаний',
    epoch: 3,
    // Используем awarenessesAtLeast как близкий прокси (после 10k тапов
    // с epoch3-апгрейдами awareness обычно ≈ 50-100k). Нет totalTaps
    // kind в текущей схеме.
    condition: { kind: 'awarenessesAtLeast', value: 50_000 },
    bonus: 0.04,
  },
  {
    id: 'L32',
    title: 'Глубокий ритм',
    epoch: 3,
    condition: { kind: 'tapsInRhythm', value: 25 },
    bonus: 0.05,
  },
  {
    id: 'L33',
    title: 'Сто кристаллизаций',
    epoch: 3,
    condition: { kind: 'crystallizationsAtLeast', value: 100 },
    bonus: 0.06,
  },
  {
    id: 'L34',
    title: 'Дважды Проводник',
    epoch: 3,
    condition: { kind: 'spiritBallsAtLeast', value: 50 },
    bonus: 0.07,
  },
  {
    id: 'L35',
    title: 'Узнавание',
    epoch: 3,
    // Тонкий урок — открывается за длинную серию (50+ подряд),
    // это медитативный hit. Hidden — игрок открывает «случайно».
    condition: { kind: 'tapsInRhythm', value: 50 },
    bonus: 0.05,
    hidden: true,
  },

  // ===== Эпоха 4 — МОГА (метод как путь) =====
  // Тема: метод вошёл в тебя, ты носитель и ученик одновременно,
  // через тебя течёт. Бонусы выше эпохи 3 — это глубокая игра, награды
  // ощутимые. Doc 02 §V прогрессия + doc 09 §V.4 пороги.
  {
    id: 'L36',
    title: 'Метод проявился',
    epoch: 4,
    // Эпоха 4 порог = ~200 кристаллизаций (точная формула в game-core
    // computeCurrentEpoch); используем proxy через crystallizations.
    condition: { kind: 'crystallizationsAtLeast', value: 200 },
    bonus: 0.06,
  },
  {
    id: 'L37',
    title: 'Чистое русло',
    epoch: 4,
    // Длинная серия в эпохе 4 — игрок уже устаканил ритм метода,
    // 100 подряд = «тело само ведёт».
    condition: { kind: 'tapsInRhythm', value: 100 },
    bonus: 0.07,
  },
  {
    id: 'L38',
    title: 'Учитель ученика',
    epoch: 4,
    // Awareness порог — игрок прошёл через тысячи тапов и
    // апгрейдов эпохи 4, накопил ~500k+.
    condition: { kind: 'awarenessesAtLeast', value: 500_000 },
    bonus: 0.08,
  },
  {
    id: 'L39',
    title: 'Полная эпоха IV',
    epoch: 4,
    condition: { kind: 'collectAllPhrasesInEpoch', epoch: 4 },
    bonus: 0.12,
  },
  {
    id: 'L40',
    title: 'Тишина мастера',
    epoch: 4,
    // 10 минут тишины — глубокая медитация мастера. Hidden,
    // открывается «случайно» когда игрок отвлёкся в эпохе 4.
    condition: { kind: 'silenceSecondsAtLeast', value: 600 },
    bonus: 0.06,
    hidden: true,
  },

  // ===== Эпоха 5 — Sai Land Heal (земля как партнёр) =====
  // Тема: место выбрало тебя, ты гость и хранитель, корни от пят,
  // дыхание леса. Эпоха ретрита: бонусы за длительность, ритм, тишину.
  {
    id: 'L41',
    title: 'Прибытие на землю',
    epoch: 5,
    // Эпоха 5 порог — 700+ кристаллизаций.
    condition: { kind: 'crystallizationsAtLeast', value: 700 },
    bonus: 0.07,
  },
  {
    id: 'L42',
    title: 'Корни',
    epoch: 5,
    // Две недели стрика — корни вросли в место. Это уже не визит,
    // а присутствие.
    condition: { kind: 'daysStreak', value: 14 },
    bonus: 0.08,
  },
  {
    id: 'L43',
    title: 'Партнёрство',
    epoch: 5,
    // Собрать половину фраз эпохи 5 — игрок «знакомится» с землёй.
    condition: { kind: 'collectPhrasesInEpoch', count: 20, epoch: 5 },
    bonus: 0.07,
  },
  {
    id: 'L44',
    title: 'Полная эпоха V',
    epoch: 5,
    condition: { kind: 'collectAllPhrasesInEpoch', epoch: 5 },
    bonus: 0.14,
  },
  {
    id: 'L45',
    title: 'Дыхание леса',
    epoch: 5,
    // 15 минут тишины — глубокое слушание леса. Hidden, награда
    // за то, что игрок прислушался к месту, а не торопился.
    condition: { kind: 'silenceSecondsAtLeast', value: 900 },
    bonus: 0.08,
    hidden: true,
  },
];
